import { useState } from "react";
import { toast } from "sonner";
import { Calculator, Loader2 } from "lucide-react";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function Shell({ title, clause, children, onRun, busy, result }) {
  return (
    <div data-testid={`calc-${title.toLowerCase().replace(/\s+/g, "-")}`}
         className="my-8 rounded-sm border border-[#D8B23E]/40 bg-[#0A0906] print:hidden">
      <div className="px-5 py-3 border-b border-[#D8B23E]/20 flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex items-center gap-2 font-grotesk text-[9px] uppercase tracking-[0.3em] text-[#D8B23E]">
          <Calculator size={12} /> Embedded Calculator · {title}
        </div>
        <span className="text-[10px] italic text-[#8A8A82] font-body">Illustrative · Clause {clause} · the Platform ledger is final</span>
      </div>
      <div className="p-5 grid md:grid-cols-[1fr_1fr] gap-6">
        <div className="space-y-3">{children}
          <button onClick={onRun} disabled={busy} data-testid="calc-run"
                  className="mt-2 inline-flex items-center gap-2 bg-[#D8B23E] text-black px-5 py-2 font-grotesk text-[9px] uppercase tracking-[0.26em] hover:bg-[#E5C450] disabled:opacity-60">
            {busy ? <Loader2 size={11} className="animate-spin" /> : null} Compute
          </button>
        </div>
        <div className="rounded-sm border border-white/10 bg-black/40 p-4 min-h-[140px]" data-testid="calc-result">{result}</div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block font-grotesk text-[8px] uppercase tracking-[0.26em] text-[#8A8A82] mb-1">{label}</span>{children}
  </label>
);
const inputCls = "w-full bg-black/60 border border-white/15 px-3 py-2 text-sm text-[#F5F5F0] font-mono focus:border-[#D8B23E] outline-none";
const Row = ({ k, v, strong }) => (
  <div className={`flex justify-between gap-4 py-1.5 border-b border-white/5 text-[13px] ${strong ? "text-[#D8B23E] font-semibold" : "text-[#C9C9C2]"}`}>
    <span className="font-body">{k}</span><span className="font-mono">{v}</span>
  </div>
);

function useCalc(api, path) {
  const [busy, setBusy] = useState(false); const [out, setOut] = useState(null);
  const run = async (body) => {
    setBusy(true);
    try { const r = await api.post(path, body); setOut(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Computation failed"); }
    finally { setBusy(false); }
  };
  return { busy, out, run };
}

export function CommissionCalc({ api }) {
  const [f, setF] = useState({ category: "cement", taxable_value: 1000000, tier_discount_pct: 0, invoice_date: "", filing_date: "" });
  const { busy, out, run } = useCalc(api, "/manual/calc/commission");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Shell title="Commission Computation" clause="12.6" busy={busy} onRun={() => run({ ...f, taxable_value: +f.taxable_value, tier_discount_pct: +f.tier_discount_pct, invoice_date: f.invoice_date || null, filing_date: f.filing_date || null })}
           result={out ? <>
             <Row k="Category rate" v={`${out.rate_pct}%`} /><Row k="Base after tier discount" v={inr(out.base_after_discount)} />
             <Row k="Gross commission" v={inr(out.gross_commission)} />
             <Row k="Filing gap / slab" v={out.gap_days == null ? "—" : `${out.gap_days} d · ${out.slab} · ${out.slab_payout_pct}%`} />
             <Row k="Payable" v={inr(out.payable)} strong />
             {out.board_escalation && <p className="mt-2 text-[11px] text-[#E5484D] font-grotesk uppercase tracking-[0.2em]">Board escalation mandatory (&gt; 45 days)</p>}
           </> : <p className="text-[12px] italic text-[#8A8A82]">Enter the taxable line value, category and dates to compute the payable commission under Clauses 12.2–12.3.</p>}>
      <Field label="Material category">
        <select className={inputCls} value={f.category} onChange={set("category")} data-testid="calc-commission-category">
          {["cement", "tmt_bars", "structural_steel", "binding_wire", "plates_sheets", "aggregate", "ready_mix_concrete", "default"].map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Taxable value (₹)"><input type="number" className={inputCls} value={f.taxable_value} onChange={set("taxable_value")} data-testid="calc-commission-value" /></Field>
        <Field label="Tier discount %"><input type="number" className={inputCls} value={f.tier_discount_pct} onChange={set("tier_discount_pct")} /></Field>
        <Field label="Invoice date"><input type="date" className={inputCls} value={f.invoice_date} onChange={set("invoice_date")} data-testid="calc-commission-invoice-date" /></Field>
        <Field label="Claim filing date"><input type="date" className={inputCls} value={f.filing_date} onChange={set("filing_date")} data-testid="calc-commission-filing-date" /></Field>
      </div>
    </Shell>
  );
}

export function MaterialCalc({ api }) {
  const [f, setF] = useState({ element: "slab", length: 10, width: 8, depth: 0.15, grade: "M20", wastage_pct: 3 });
  const { busy, out, run } = useCalc(api, "/manual/calc/material");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const isMasonry = f.element === "wall" || f.element === "plaster";
  return (
    <Shell title="Material Quantity Estimator" clause="22.5" busy={busy} onRun={() => run({ ...f, length: +f.length, width: +f.width, depth: +f.depth, wastage_pct: +f.wastage_pct })}
           result={out ? <>
             {out.wet_volume_m3 != null && <Row k="Wet volume" v={`${out.wet_volume_m3} m³`} />}
             {out.dry_volume_m3 != null && <Row k="Dry volume (×1.54)" v={`${out.dry_volume_m3} m³`} />}
             {out.mix && <Row k="Nominal mix" v={`${out.grade} · ${out.mix}`} />}
             {out.bricks != null && <Row k="Bricks (incl. wastage)" v={out.bricks.toLocaleString("en-IN")} />}
             <Row k="Cement" v={`${out.cement_bags} bags (50 kg)`} strong />
             <Row k="Sand" v={`${out.sand_m3} m³`} />
             {out.aggregate_m3 != null && <Row k="Coarse aggregate" v={`${out.aggregate_m3} m³`} />}
             {out.steel_kg != null && <Row k={`Steel (${out.steel_rule_kg_per_m3} kg/m³ rule)`} v={`${out.steel_kg} kg`} />}
             {out.mortar && <Row k="Mortar" v={out.mortar} />}
           </> : <p className="text-[12px] italic text-[#8A8A82]">Select the element and enter dimensions in metres to derive a bill of quantities under Clauses 22.2–22.4.</p>}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Element">
          <select className={inputCls} value={f.element} onChange={set("element")} data-testid="calc-material-element">
            {["slab", "beam", "column", "footing", "staircase", "wall", "plaster"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        {!isMasonry && <Field label="Concrete grade">
          <select className={inputCls} value={f.grade} onChange={set("grade")}>{["M10", "M15", "M20", "M25", "M30"].map((g) => <option key={g}>{g}</option>)}</select>
        </Field>}
        <Field label="Length (m)"><input type="number" step="0.01" className={inputCls} value={f.length} onChange={set("length")} data-testid="calc-material-length" /></Field>
        <Field label={f.element === "plaster" ? "Height (m)" : "Width (m)"}><input type="number" step="0.01" className={inputCls} value={f.width} onChange={set("width")} /></Field>
        {f.element !== "plaster" && <Field label={f.element === "wall" ? "Thickness (m)" : "Depth (m)"}><input type="number" step="0.01" className={inputCls} value={f.depth} onChange={set("depth")} /></Field>}
        <Field label="Wastage %"><input type="number" className={inputCls} value={f.wastage_pct} onChange={set("wastage_pct")} /></Field>
      </div>
    </Shell>
  );
}

export function RateCompareCalc({ api }) {
  const [items, setItems] = useState([
    { name: "Brand A · OPC 53", rate: 380, freight: 12, gst_pct: 28, wastage_pct: 2 },
    { name: "Brand B · OPC 53", rate: 365, freight: 20, gst_pct: 28, wastage_pct: 2 },
    { name: "Index-linked quote", rate: 372, freight: 15, gst_pct: 28, wastage_pct: 2 },
  ]);
  const { busy, out, run } = useCalc(api, "/manual/calc/rate-comparison");
  const upd = (i, k) => (e) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)));
  return (
    <Shell title="Rate Comparison" clause="30.5" busy={busy} onRun={() => run({ items: items.map((i) => ({ ...i, rate: +i.rate, freight: +i.freight, gst_pct: +i.gst_pct, wastage_pct: +i.wastage_pct })) })}
           result={out ? <>
             {out.rows.map((r) => <Row key={r.name} k={`${r.name}${r.is_best ? " · BEST" : ""}`} v={`${inr(r.landed_per_unit)} (${r.delta_pct_vs_best >= 0 ? "+" : ""}${r.delta_pct_vs_best}%)`} strong={r.is_best} />)}
             <p className="mt-2 text-[10px] text-[#8A8A82] font-mono">{out.formula}</p>
           </> : <p className="text-[12px] italic text-[#8A8A82]">Compare landed cost per unit across brands or against an index-linked quotation.</p>}>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-5 gap-2">
            <input className={inputCls + " col-span-1"} value={it.name} onChange={upd(i, "name")} data-testid={`calc-rate-name-${i}`} />
            <input type="number" className={inputCls} value={it.rate} onChange={upd(i, "rate")} placeholder="Rate" data-testid={`calc-rate-rate-${i}`} />
            <input type="number" className={inputCls} value={it.freight} onChange={upd(i, "freight")} placeholder="Freight" />
            <input type="number" className={inputCls} value={it.gst_pct} onChange={upd(i, "gst_pct")} placeholder="GST %" />
            <input type="number" className={inputCls} value={it.wastage_pct} onChange={upd(i, "wastage_pct")} placeholder="Waste %" />
          </div>
        ))}
        <div className="font-grotesk text-[8px] uppercase tracking-[0.22em] text-[#8A8A82]">Columns: Option · Rate · Freight · GST % · Wastage %</div>
      </div>
    </Shell>
  );
}

export function ProjectCostCalc({ api }) {
  const [f, setF] = useState({ built_up_sqft: 5000, structure: "rcc_frame", finish: "standard", city: "metro", contingency_pct: 5, gst_pct: 18 });
  const { busy, out, run } = useCalc(api, "/manual/calc/project-cost");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Shell title="Project Cost Estimator" clause="22.6" busy={busy} onRun={() => run({ ...f, built_up_sqft: +f.built_up_sqft, contingency_pct: +f.contingency_pct, gst_pct: +f.gst_pct })}
           result={out ? <>
             <Row k="Composite rate" v={`${inr(out.rate_per_sqft)} / sq ft`} /><Row k="Base cost" v={inr(out.base_cost)} />
             {out.heads.map((h) => <Row key={h.head} k={`${h.head} (${h.share_pct}%)`} v={inr(h.amount)} />)}
             <Row k={`Contingency ${out.assumptions.contingency_pct}%`} v={inr(out.contingency)} /><Row k={`GST ${out.assumptions.gst_pct}%`} v={inr(out.gst)} />
             <Row k="Estimated project cost" v={inr(out.total)} strong />
           </> : <p className="text-[12px] italic text-[#8A8A82]">Enter the built-up area and classification to derive a preliminary estimate under Clause 22.6.</p>}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Built-up area (sq ft)"><input type="number" className={inputCls} value={f.built_up_sqft} onChange={set("built_up_sqft")} data-testid="calc-project-area" /></Field>
        <Field label="Structure"><select className={inputCls} value={f.structure} onChange={set("structure")}>{["rcc_frame", "load_bearing", "steel_frame"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select></Field>
        <Field label="Finish class"><select className={inputCls} value={f.finish} onChange={set("finish")}>{["standard", "premium", "luxury"].map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Location factor"><select className={inputCls} value={f.city} onChange={set("city")}>{["metro", "tier1", "tier2", "tier3"].map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Contingency %"><input type="number" className={inputCls} value={f.contingency_pct} onChange={set("contingency_pct")} /></Field>
        <Field label="GST %"><input type="number" className={inputCls} value={f.gst_pct} onChange={set("gst_pct")} /></Field>
      </div>
    </Shell>
  );
}

export const CALCULATORS = { commission: CommissionCalc, material: MaterialCalc, rate_comparison: RateCompareCalc, project_cost: ProjectCostCalc };
